import { observer } from 'mobx-react';
import type { FC } from 'react';
import { useCallback, useEffect, useState, memo } from 'react';
import API from '@api';
import RecipeActions from './components/RecipeActions';
import { LazyLoadImage } from '@components/reactLazyLoadImageComponent';
import isEmpty from 'lodash/isEmpty';
import { notifications } from '@utils/notifications';
import BannersLink from '@components/BannersLink';
import LoadingSkeleton from '@components/LoadingSkeleton';
import UtilsTime from '@utils/time';
import UIicons from '@ui-kit';
import { createImageErrorHandler } from '@utils/onImageError';
import { useHistory, useLocation } from '@compat/router';
import { declension } from '@utils/declension';
import icons from '@icons';
import { Picture } from '@ui';
import { useStores } from '@root/src/stores';
import './index.scss';

const {
  star: { yellow: starYellowFilled },
  timer: { darkGrey: timerDarkGreyFilled, red: timerRedFilled },
  person: { darkGrey: personDarkGreyFilled },
  messageCircle: { darkGrey: messageCircleDGFilled }
} = icons.Fill;

const {
  cutlery: { darkGrey: cutleryDGOutlined }
} = icons.Outline;
const { productCardErrorImg } = UIicons;

const getDifficulty = (value: number): string => {
  if (value === 1) {
    return 'Очень простой';
  }
  if (value === 2) {
    return 'Простой';
  }
  if (value === 3) {
    return 'Сложный';
  }
  if (value === 4) {
    return 'Очень сложный';
  }
  return 'Простой';
};

const onErrorHandler = createImageErrorHandler(productCardErrorImg.x3);

interface RecipeCardProps {
  className?: string;
  isRecipeHorizontal?: boolean;
  item;
}

interface RecipeCardLoadingProps {
  isRecipeHorizontal?: RecipeCardProps['isRecipeHorizontal'];
}

export const RecipeCardLoading: FC<RecipeCardLoadingProps> = memo(props => {
  const { isRecipeHorizontal = false } = props;

  return (
    <div className='RecipeCard'>
      <div
        className={`RecipeCard__link ${
          isRecipeHorizontal ? 'RecipeCard__link--horizontal' : ''
        }`}
      >
        <div className='RecipeCard__img-block'>
          <LoadingSkeleton className='RecipeCard__img-block__img !rounded-[12px]' />
        </div>
        <div className='RecipeCard__info-block'>
          <div className='RecipeCard__title'>
            <LoadingSkeleton className='h-[1lh] w-full' />
            <LoadingSkeleton className='h-[1lh] w-3/4' />
          </div>
          <div className='RecipeCard__info-block__bottom'>
            <div className='RecipeNumbers' />
            <div className='RecipeCard__info-wrapper'>
              <LoadingSkeleton className='h-[16px] w-3/4' />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export const RecipeCard: FC<RecipeCardProps> = observer(
  ({ item, isRecipeHorizontal = false, className }) => {
    const { modalsStore, userStore } = useStores();
    const { changeAuthModalState } = modalsStore;
    const { user } = userStore;
    const history = useHistory();
    const location = useLocation();

    const [isFavorite, setIsFavorite] = useState(false);

    useEffect(() => {
      setIsFavorite(!!item?.isFavorite);
    }, [item?.isFavorite]);

    const recipeLink = `/recipes/${item.identifyIdURI}`;
    const commentText = `${item?.comments_number || '0'}`;
    const cookingTime = UtilsTime.minutesToStr(item?.cooking_time);

    const onCommentlickHandler = useCallback(
      e => {
        e.stopPropagation();
        history.push(`/recipes/${item.identifyIdURI}`, {
          isScrollToComments: true
        });
      },
      [history, item.identifyIdURI]
    );

    const isBludoPage =
      location.pathname === `/recipes/r/bludo/${item.bludoItem?.id ?? ''}`;

    const onCategoryClickHandler = useCallback(
      e => {
        if (item?.bludoItem?.id && !isBludoPage) {
          e.preventDefault();
          e.stopPropagation();
          history.push(`/recipes/r/bludo/${item.bludoItem?.id ?? ''}`);
        }
      },
      [history, isBludoPage, item.bludoItem?.id]
    );

    const addRecipeToFavoritesList = useCallback(() => {
      API.addRecipeToFavoritesList({
        data: { objectId: +item.id }
      }).then(() => {
        setIsFavorite(true);
      });
    }, [item.id]);

    const removeRecipeFromFavoritesList = useCallback(() => {
      API.removeRecipeFromFavoritesList({
        data: { objectId: +item.id }
      }).then(() => {
        setIsFavorite(false);
      });
    }, [item.id]);

    const handleRecipeFavorites = useCallback(() => {
      if (isEmpty(user)) {
        changeAuthModalState();
      } else {
        if (isFavorite) {
          removeRecipeFromFavoritesList();
          notifications('Рецент успешно удален из избранного', '');
        } else {
          addRecipeToFavoritesList();
          notifications('Рецепт успешно добавлен в избранное', '');
        }
      }
    }, [
      addRecipeToFavoritesList,
      changeAuthModalState,
      isFavorite,
      removeRecipeFromFavoritesList,
      user
    ]);

    if (!item) {
      return null;
    }
    return (
      <div className={`RecipeCard ${className || ''}`}>
        <RecipeActions
          isFavorite={isFavorite}
          link={item.identifyIdURI}
          favoriteHandler={handleRecipeFavorites}
        />
        <BannersLink
          className={`RecipeCard__link${
            isRecipeHorizontal ? ' RecipeCard__link--horizontal' : ''
          }`}
          link={recipeLink}
        >
          {item.bludoItem?.title && (
            <div
              onClick={onCategoryClickHandler}
              className={`RecipeCard__category${
                isBludoPage ? ' RecipeCard__category--no-hover' : ''
              }`}
            >
              {item.bludoItem?.title}
            </div>
          )}
          {
            <div className='RecipeCard__img-block'>
              {!!item?.is_product_promo && (
                <div className='text-sm leading-[18px] absolute z-10 px-2 py-1 bottom-4 left-2 rounded-lg flex items-center bg-[var(--mid-yellow)]'>
                  <Picture
                    icon={timerRedFilled}
                    width={16}
                    height={16}
                    pictureClassName={'mr-1'}
                  />
                  5 минут
                </div>
              )}
              <LazyLoadImage
                onError={onErrorHandler}
                width='340'
                height='220'
                className='RecipeCard__img-block__img'
                src={item.attachmentsItem.cacheItem[0].filePath}
                alt={item?.title ? `${item.title} рецепт` : 'Рецепт'}
              />
            </div>
          }
          <div className='RecipeCard__info-block'>
            <p className='RecipeCard__title'>{item?.title}</p>
            <div className='RecipeCard__info-block__bottom'>
              <div className='RecipeNumbers' />
              <div className='RecipeCard__info-wrapper'>
                <div className='RecipeCard__info'>
                  <Picture
                    icon={starYellowFilled}
                    height='16'
                    width='16'
                    alt='звезда'
                  />
                  {item.rating}
                </div>
                {isRecipeHorizontal && (
                  <div className='RecipeCard__info'>
                    <Picture
                      icon={cutleryDGOutlined}
                      height='16'
                      width='16'
                      alt='часы'
                    />
                    {getDifficulty(item.recipes_difficult)}
                  </div>
                )}
                <div className='RecipeCard__info'>
                  <Picture
                    icon={timerDarkGreyFilled}
                    height='16'
                    width='16'
                    alt='часы'
                  />
                  {cookingTime}
                </div>
                <div className='RecipeCard__info RecipeCard__info--people'>
                  <Picture
                    icon={personDarkGreyFilled}
                    height='16'
                    width='16'
                    alt='пользователь'
                  />
                  <div>
                    {item?.serving}{' '}
                    <span className='RecipeCard__info__text'>
                      {declension(item?.serving, [
                        'порция',
                        'порции',
                        'порций'
                      ])}
                    </span>
                  </div>
                </div>
                <div
                  onClick={onCommentlickHandler}
                  className='RecipeCard__info RecipeCard__info--comment'
                >
                  <Picture
                    icon={messageCircleDGFilled}
                    height='16'
                    width='16'
                    alt='пользователь'
                  />
                  {commentText}
                </div>
              </div>
            </div>
            {isRecipeHorizontal && (
              <div className='RecipeCard__author'>
                Автор:{' '}
                {item.authorItem
                  ? `${item.authorItem.fio}, ${item.authorItem.title}`
                  : ''}
              </div>
            )}
          </div>
        </BannersLink>
      </div>
    );
  }
);
